import React, { useState, useEffect, useMemo } from 'react';
import { 
  FaChalkboardTeacher, 
  FaSave, 
  FaPlus, 
  FaEdit, 
  FaTrash, 
  FaSpinner,
  FaSearch,
  FaTimes,
  FaFileImport,
  FaCheckCircle,
  FaExclamationCircle
} from 'react-icons/fa';
import { API_BASE_URL } from '../utils/apiConfig';

interface TeacherReportSetting {
  _id?: string;
  course: string;
  courseCode: string;
  spanishTeacher: string;
  englishTeacher: string;
  additionalTeachers: Array<{
    name: string;
    role: string;
  }>;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface TeacherReportSettingsProps {
  onSectionChange?: (section: string) => void;
}

const TeacherReportSettings: React.FC<TeacherReportSettingsProps> = ({ onSectionChange }) => {
  const [settings, setSettings] = useState<TeacherReportSetting[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info', text: string } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState<TeacherReportSetting>({
    course: '',
    courseCode: '',
    spanishTeacher: '',
    englishTeacher: '',
    additionalTeachers: [],
    active: true
  });

  const getToken = () => localStorage.getItem('token');

  // Load settings from API
  const loadSettings = async () => {
    setLoading(true);
    try {
      const token = getToken();
      if (!token) {
        setMessage({ type: 'error', text: 'No hay sesión activa' });
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/teacher-reports/settings`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Error al cargar configuraciones');
      }

      const result = await response.json();
      if (result.success) {
        setSettings(result.data || []);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      setMessage({ type: 'error', text: 'Error al cargar la configuración de docentes' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  // Show message with auto-hide
  const showMessage = (type: 'success' | 'error' | 'info', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  // Save or update setting
  const handleSave = async () => {
    // Validaciones
    if (!formData.course.trim()) {
      showMessage('error', 'El nombre del curso es requerido');
      return;
    }
    if (!formData.spanishTeacher.trim()) {
      showMessage('error', 'El profesor principal es requerido');
      return;
    }

    setSaving(true);
    try {
      const token = getToken();
      if (!token) {
        showMessage('error', 'No hay sesión activa');
        return;
      }

      const url = editingId 
        ? `${API_BASE_URL}/api/teacher-reports/settings/${editingId}`
        : `${API_BASE_URL}/api/teacher-reports/settings`;
      
      const method = editingId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al guardar');
      }

      const result = await response.json();
      if (result.success) {
        showMessage('success', editingId ? 'Configuración actualizada exitosamente' : 'Configuración creada exitosamente');
        await loadSettings();
        resetForm();
      }
    } catch (error: any) {
      console.error('Error saving setting:', error);
      showMessage('error', error.message || 'Error al guardar la configuración');
    } finally {
      setSaving(false);
    }
  };

  // Delete setting
  const handleDelete = async (id: string) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta configuración?')) {
      return;
    }

    try {
      const token = getToken();
      if (!token) {
        showMessage('error', 'No hay sesión activa');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/teacher-reports/settings/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Error al eliminar');
      }

      const result = await response.json();
      if (result.success) {
        showMessage('success', 'Configuración eliminada exitosamente');
        await loadSettings();
      }
    } catch (error: any) {
      console.error('Error deleting setting:', error);
      showMessage('error', error.message || 'Error al eliminar la configuración');
    }
  };

  // Edit setting
  const handleEdit = (setting: TeacherReportSetting) => {
    setFormData({
      course: setting.course,
      courseCode: setting.courseCode,
      spanishTeacher: setting.spanishTeacher,
      englishTeacher: setting.englishTeacher,
      additionalTeachers: setting.additionalTeachers || [],
      active: setting.active
    });
    setEditingId(setting._id || null);
    setShowAddForm(true);
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      course: '',
      courseCode: '',
      spanishTeacher: '',
      englishTeacher: '',
      additionalTeachers: [],
      active: true
    });
    setEditingId(null);
    setShowAddForm(false);
  };

  // Add additional teacher
  const addAdditionalTeacher = () => {
    setFormData({
      ...formData,
      additionalTeachers: [
        ...formData.additionalTeachers,
        { name: '', role: '' }
      ]
    });
  };

  // Remove additional teacher
  const removeAdditionalTeacher = (index: number) => {
    const newAdditionalTeachers = [...formData.additionalTeachers];
    newAdditionalTeachers.splice(index, 1);
    setFormData({
      ...formData,
      additionalTeachers: newAdditionalTeachers
    });
  };

  // Update additional teacher
  const updateAdditionalTeacher = (index: number, field: 'name' | 'role', value: string) => {
    const newAdditionalTeachers = [...formData.additionalTeachers];
    newAdditionalTeachers[index][field] = value;
    setFormData({
      ...formData,
      additionalTeachers: newAdditionalTeachers
    });
  };

  // Función para ordenar cursos correctamente
  const sortCourses = (a: TeacherReportSetting, b: TeacherReportSetting): number => {
    const courseOrder = [
      'Medio Mayor A', 'Medio Mayor B', 'Medio Mayor C', 'Medio Mayor D',
      'Pre Kinder A', 'Pre Kinder B', 'Pre Kinder C',
      'Kinder A', 'Kinder B', 'Kinder C',
      'Primero básico A', 'Primero básico B', 'Primero básico C',
      'Segundo básico A', 'Segundo básico B', 'Segundo básico C',
      'Tercero básico A', 'Tercero básico B', 'Tercero básico C',
      'Cuarto básico A', 'Cuarto básico B', 'Cuarto básico C',
      'Quinto básico A', 'Quinto básico B', 'Quinto básico C',
      'Sexto básico A', 'Sexto básico B', 'Sexto básico C'
    ];

    // Normalizar nombres para comparación (eliminar "Nivel" y variaciones)
    const normalizeCourse = (course: string) => {
      return course
        .replace(/^Nivel\s+/i, '')
        .replace(/medio\s+mayor/i, 'Medio Mayor')
        .replace(/prekínder/i, 'Pre Kinder')
        .replace(/kínder/i, 'Kinder')
        .trim();
    };

    const normalizedA = normalizeCourse(a.course);
    const normalizedB = normalizeCourse(b.course);

    const indexA = courseOrder.findIndex(c => c.toLowerCase() === normalizedA.toLowerCase());
    const indexB = courseOrder.findIndex(c => c.toLowerCase() === normalizedB.toLowerCase());

    // Si ambos están en el orden definido
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }
    // Si solo A está en el orden, va primero
    if (indexA !== -1) return -1;
    // Si solo B está en el orden, va primero
    if (indexB !== -1) return 1;
    // Si ninguno está en el orden, ordenar alfabéticamente
    return a.course.localeCompare(b.course);
  };

  // Filtered and sorted settings for search
  const filteredSettings = useMemo(() => {
    let filtered = settings;
    
    // Aplicar filtro de búsqueda
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = settings.filter(setting => 
        setting.course.toLowerCase().includes(term) ||
        setting.courseCode.toLowerCase().includes(term) ||
        setting.spanishTeacher.toLowerCase().includes(term) ||
        setting.englishTeacher.toLowerCase().includes(term)
      );
    }
    
    // Ordenar por cursos
    return [...filtered].sort(sortCourses);
  }, [settings, searchTerm]);

  // Import default data
  const handleImportDefaults = async () => {
    if (!window.confirm('¿Deseas importar los docentes por defecto? Esto creará configuraciones para todos los cursos que no tengan docentes asignados.')) {
      return;
    }

    setSaving(true);
    try {
      const token = getToken();
      if (!token) {
        showMessage('error', 'No hay sesión activa');
        return;
      }

      // Datos por defecto basados en el hardcode actual
      const defaultData = [
        { course: "Nivel medio mayor A", courseCode: "MMA", spanishTeacher: "Josefa Crovetto", englishTeacher: "Valentina Thienemann" },
        { course: "Nivel medio mayor B", courseCode: "MMB", spanishTeacher: "Rossana Díaz", englishTeacher: "Nicole Larrea" },
        { course: "Nivel medio mayor C", courseCode: "MMC", spanishTeacher: "Bernardita Suazo", englishTeacher: "María Jesús Valdivieso" },
        { course: "Nivel medio mayor D", courseCode: "MMD", spanishTeacher: "María Jesús Jerez", englishTeacher: "Francisca Portaluppi" },
        { course: "Prekínder A", courseCode: "PKA", spanishTeacher: "Camila Oyarzo", englishTeacher: "Teresa Echeverría" },
        { course: "Prekínder B", courseCode: "PKB", spanishTeacher: "Vanessa Almonacid", englishTeacher: "Pilar Plaza de los Reyes" },
        { course: "Prekínder C", courseCode: "PKC", spanishTeacher: "Carola Meersohn", englishTeacher: "Marcela Moreno" },
        { course: "Kínder A", courseCode: "KA", spanishTeacher: "Magdalena Undurraga", englishTeacher: "Camila Álvarez" },
        { course: "Kínder B", courseCode: "KB", spanishTeacher: "Constanza Moguillansky", englishTeacher: "Carolina Krause" },
        { course: "Kínder C", courseCode: "KC", spanishTeacher: "Isidora Barrientos", englishTeacher: "Camila Mejías" },
        { course: "Primero básico A", courseCode: "1BA", spanishTeacher: "Paula Larraín", englishTeacher: "Josefina Durán" },
        { course: "Primero básico B", courseCode: "1BB", spanishTeacher: "Rebeca Pizarro", englishTeacher: "Danitza Munjin" },
        { course: "Primero básico C", courseCode: "1BC", spanishTeacher: "Carolina Alfaro", englishTeacher: "Tamara Torres" },
        { course: "Segundo básico A", courseCode: "2BA", spanishTeacher: "Dolores Ugalde", englishTeacher: "Daniela Herrera" },
        { course: "Segundo básico B", courseCode: "2BB", spanishTeacher: "Magdalena Izquierdo", englishTeacher: "Catalina Werner" },
        { course: "Segundo básico C", courseCode: "2BC", spanishTeacher: "Soledad Parada", englishTeacher: "Magdalena Latorre" },
        { course: "Tercero básico A", courseCode: "3BA", spanishTeacher: "Bernardita Montero", englishTeacher: "Macarena Gutiérrez" },
        { course: "Tercero básico C", courseCode: "3BC", spanishTeacher: "Javiera Montero", englishTeacher: "Javiera Montero" }
      ];

      const response = await fetch(`${API_BASE_URL}/api/teacher-reports/bulk-import`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ teachersData: defaultData })
      });

      if (!response.ok) {
        throw new Error('Error al importar datos');
      }

      const result = await response.json();
      if (result.success) {
        showMessage('success', `Importación exitosa: ${result.data.upserted} creados, ${result.data.modified} actualizados`);
        await loadSettings();
      }
    } catch (error: any) {
      console.error('Error importing defaults:', error);
      showMessage('error', error.message || 'Error al importar los datos por defecto');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="text-center py-8">
          <FaSpinner className="animate-spin text-4xl text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Cargando configuración...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <FaChalkboardTeacher className="text-2xl text-blue-600 mr-3" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Docentes en Informes</h1>
            <p className="text-sm text-gray-600">Configura qué docentes aparecen en los informes PDF de cada curso</p>
          </div>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => {
              resetForm();
              setShowAddForm(true);
            }}
            className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <FaPlus />
            <span>Agregar Curso</span>
          </button>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`mb-6 p-4 rounded-lg flex items-center justify-between ${
          message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' :
          message.type === 'error' ? 'bg-red-50 text-red-800 border border-red-200' :
          'bg-blue-50 text-blue-800 border border-blue-200'
        }`}>
          <div className="flex items-center">
            {message.type === 'success' ? <FaCheckCircle className="mr-2" /> : <FaExclamationCircle className="mr-2" />}
            <span>{message.text}</span>
          </div>
          <button onClick={() => setMessage(null)} className="text-gray-500 hover:text-gray-700">
            <FaTimes />
          </button>
        </div>
      )}

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="mb-6 p-6 border-2 border-blue-300 rounded-lg bg-blue-50">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">
              {editingId ? 'Editar Configuración' : 'Agregar Nueva Configuración'}
            </h2>
            <button
              onClick={resetForm}
              className="text-gray-500 hover:text-gray-700"
            >
              <FaTimes className="text-xl" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nombre del Curso *
              </label>
              <input
                type="text"
                value={formData.course}
                onChange={(e) => setFormData({ ...formData, course: e.target.value })}
                placeholder="Ej: Primero básico A"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Código del Curso
              </label>
              <input
                type="text"
                value={formData.courseCode}
                onChange={(e) => setFormData({ ...formData, courseCode: e.target.value.toUpperCase() })}
                placeholder="Ej: 1BA"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Profesor Principal *
              </label>
              <input
                type="text"
                value={formData.spanishTeacher}
                onChange={(e) => setFormData({ ...formData, spanishTeacher: e.target.value })}
                placeholder="Nombre completo del profesor principal"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Profesor Secundario
              </label>
              <input
                type="text"
                value={formData.englishTeacher}
                onChange={(e) => setFormData({ ...formData, englishTeacher: e.target.value })}
                placeholder="Nombre del profesor secundario (opcional)"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Additional Teachers */}
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Profesores Adicionales
              </label>
              <button
                onClick={addAdditionalTeacher}
                className="text-sm text-blue-600 hover:text-blue-700 flex items-center"
              >
                <FaPlus className="mr-1" />
                Agregar
              </button>
            </div>
            
            {formData.additionalTeachers.map((teacher, index) => (
              <div key={index} className="flex space-x-2 mb-2">
                <input
                  type="text"
                  value={teacher.name}
                  onChange={(e) => updateAdditionalTeacher(index, 'name', e.target.value)}
                  placeholder="Nombre del profesor"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="text"
                  value={teacher.role}
                  onChange={(e) => updateAdditionalTeacher(index, 'role', e.target.value)}
                  placeholder="Rol (Ej: Música, Ed. Física)"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={() => removeAdditionalTeacher(index)}
                  className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                >
                  <FaTrash />
                </button>
              </div>
            ))}
          </div>

          {/* Active checkbox */}
          <div className="mt-4 flex items-center">
            <input
              type="checkbox"
              id="active"
              checked={formData.active}
              onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
              className="mr-2"
            />
            <label htmlFor="active" className="text-sm text-gray-700">
              Activo (aparecerá en los informes PDF)
            </label>
          </div>

          {/* Form Actions */}
          <div className="mt-6 flex space-x-2">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {saving ? <FaSpinner className="animate-spin" /> : <FaSave />}
              <span>{saving ? 'Guardando...' : 'Guardar'}</span>
            </button>
            <button
              onClick={resetForm}
              disabled={saving}
              className="px-6 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-lg transition-colors"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="mb-4">
        <div className="relative">
          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar por curso, código o nombre de docente..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Settings List */}
      {filteredSettings.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <FaChalkboardTeacher className="text-4xl mx-auto mb-4 opacity-50" />
          <p>No hay configuraciones de docentes disponibles</p>
          <p className="text-sm mt-2">Haz clic en "Agregar Curso" para comenzar</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b-2 border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Curso
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Código
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Profesor Principal
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Profesor Secundario
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredSettings.map((setting) => (
                <tr key={setting._id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">
                    {setting.course}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {setting.courseCode || '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {setting.spanishTeacher}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {setting.englishTeacher || '-'}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      setting.active 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {setting.active ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-right space-x-2">
                    <button
                      onClick={() => handleEdit(setting)}
                      className="text-blue-600 hover:text-blue-800 inline-flex items-center"
                    >
                      <FaEdit className="mr-1" />
                      Editar
                    </button>
                    <button
                      onClick={() => setting._id && handleDelete(setting._id)}
                      className="text-red-600 hover:text-red-800 inline-flex items-center ml-2"
                    >
                      <FaTrash className="mr-1" />
                      Eliminar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TeacherReportSettings;

// Export adicional para asegurar que sea reconocido como módulo
export { TeacherReportSettings };

